import { supabase } from './supabase';
import { BowelMovement } from '../types';

// Konwersja między formatem aplikacji (camelCase) a formatem bazy danych (snake_case)
const toDbFormat = (data: BowelMovement) => {
  const result: any = {
    id: data.id,
    date: data.date,
    time: data.time,
    consistency: data.consistency,
    has_blood: data.hasBlood,
    has_mucus: data.hasMucus,
    pain: data.pain,
    amount: data.amount,
    has_gas: data.hasGas,
    urgency: data.urgency || 1, // Domyślnie 1 jeśli nie ustawione (CHECK wymaga 1-5)
    other_symptoms: data.otherSymptoms || ''
  };
  
  // Dodaj blood_amount tylko jeśli hasBlood jest true i wartość jest w zakresie 1-5
  if (data.hasBlood && data.bloodAmount && data.bloodAmount >= 1 && data.bloodAmount <= 5) {
    result.blood_amount = data.bloodAmount;
  }
  
  // Dodaj blood_type tylko jeśli hasBlood jest true
  if (data.hasBlood && data.bloodType) {
    result.blood_type = data.bloodType;
  }
  
  // Dodaj mucus_amount tylko jeśli hasMucus jest true i wartość jest w zakresie 1-5
  if (data.hasMucus && data.mucusAmount && data.mucusAmount >= 1 && data.mucusAmount <= 5) {
    result.mucus_amount = data.mucusAmount;
  }
  
  // Dodaj gas_intensity tylko jeśli hasGas jest true i wartość jest w zakresie 1-5
  if (data.hasGas && data.gasIntensity && data.gasIntensity >= 1 && data.gasIntensity <= 5) {
    result.gas_intensity = data.gasIntensity;
  }
  
  return result;
};

const fromDbFormat = (dbData: any): BowelMovement => ({
  id: dbData.id,
  date: dbData.date,
  time: dbData.time,
  consistency: dbData.consistency,
  hasBlood: dbData.has_blood,
  bloodAmount: dbData.blood_amount,
  bloodType: dbData.blood_type,
  hasMucus: dbData.has_mucus,
  mucusAmount: dbData.mucus_amount,
  pain: dbData.pain,
  amount: dbData.amount,
  hasGas: dbData.has_gas,
  gasIntensity: dbData.gas_intensity,
  urgency: dbData.urgency,
  otherSymptoms: dbData.other_symptoms || ''
});

export const bowelMovementService = {
  // Pobierz wszystkie wpisy dla zalogowanego użytkownika
  async getAll(): Promise<BowelMovement[]> {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      throw new Error('Użytkownik nie jest zalogowany');
    }

    const { data, error } = await supabase
      .from('bowel_movements')
      .select('*')
      .eq('user_id', user.id)
      .order('date', { ascending: false })
      .order('time', { ascending: false });

    if (error) {
      throw error;
    }

    return (data || []).map(fromDbFormat);
  },

  // Dodaj nowy wpis
  async create(data: BowelMovement): Promise<BowelMovement> {
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    
    if (userError || !user) {
      console.error('Błąd autoryzacji:', userError);
      throw new Error('Użytkownik nie jest zalogowany');
    }

    const dbData = {
      ...toDbFormat(data),
      user_id: user.id
    };
    
    // Usuń undefined/null wartości, które mogą powodować problemy
    Object.keys(dbData).forEach(key => {
      if (dbData[key] === undefined) {
        delete dbData[key];
      }
    });
    
    console.log('Zapisywanie wypróżnienia:', dbData);
    console.log('User ID:', user.id);
    
    const { data: insertedData, error } = await supabase
      .from('bowel_movements')
      .insert([dbData])
      .select()
      .single();

    if (error) {
      console.error('Błąd zapisywania wypróżnienia:', error);
      console.error('Szczegóły błędu:', {
        message: error.message,
        details: error.details,
        hint: error.hint,
        code: error.code
      });
      console.error('Dane które próbowano zapisać:', JSON.stringify(dbData, null, 2));
      console.error('User ID:', user.id);
      
      // Przyjazniejszy komunikat błędu dla użytkownika
      let errorMessage = 'Nie udało się zapisać danych.';
      
      // Obsługa różnych typów błędów
      if (error.code === '23505') {
        errorMessage = 'Ten wpis już istnieje. Spróbuj odświeżyć stronę.';
      } else if (error.code === '23514') {
        errorMessage = 'Nieprawidłowa wartość danych. Sprawdź wprowadzone wartości.';
      } else if (error.code === 'PGRST301' || error.message?.includes('JWT')) {
        errorMessage = 'Sesja wygasła. Zaloguj się ponownie.';
      } else if (error.message?.includes('network') || error.message?.includes('fetch')) {
        errorMessage = 'Brak połączenia z internetem. Sprawdź połączenie i spróbuj ponownie.';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      if (error.hint && !errorMessage.includes(error.hint)) {
        errorMessage += ` Wskazówka: ${error.hint}`;
      }
      
      throw new Error(errorMessage);
    }

    console.log('Wypróżnienie zapisane pomyślnie:', insertedData);
    return fromDbFormat(insertedData);
  },

  // Zaktualizuj wpis
  async update(id: string, data: BowelMovement): Promise<BowelMovement> {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      throw new Error('Użytkownik nie jest zalogowany');
    }

    const dbData = toDbFormat(data);
    const { data: updatedData, error } = await supabase
      .from('bowel_movements')
      .update(dbData)
      .eq('id', id)
      .eq('user_id', user.id) // Upewnij się, że użytkownik może edytować tylko swoje wpisy
      .select()
      .single();

    if (error) {
      throw error;
    }

    return fromDbFormat(updatedData);
  },

  // Usuń wpis
  async delete(id: string): Promise<void> {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      throw new Error('Użytkownik nie jest zalogowany');
    }

    const { error } = await supabase
      .from('bowel_movements')
      .delete()
      .eq('id', id)
      .eq('user_id', user.id); // Upewnij się, że użytkownik może usuwać tylko swoje wpisy

    if (error) {
      throw error;
    }
  }
};
